import bindAllOfThis from 'src/utils/BindThisHelper';
import { TiledGameObject, PhaserGeneralGameObjectType } from 'src/games/shared/phaserHelpers/HandyTypes';
import initializeInteractiveCursor from '../../shared/InteractiveCursorUtil';
import { CommandSlot } from '../objects/CommandSlot';
import { Command } from '../objects/Command';
import { PhaserButton, PhaserButtonConfig } from '../../shared/Button';
import { Color } from 'src/games/shared/ColorValue';
import TranslateApi from 'src/utils/TranslateHelper';
import { PhaserModalConfig, PhaserModal } from '../../shared/Modal';
import { Arrow } from 'src/games/ProgramRobotGame/objects/Arrow';
import { EndGameScene } from 'src/games/ProgramRobotGame/scenes/EndGameScene';

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    map: Phaser.Tilemaps.Tilemap;
    commandSlots: CommandSlot[];
    commands: Command[];
    doneButton: Phaser.GameObjects.Sprite;
    deleteArea: Phaser.Geom.Rectangle;
    arrows: Arrow[];
    robot: Phaser.GameObjects.Sprite;
    follower: Phaser.GameObjects.PathFollower;
    points: Phaser.Math.Vector2[];
    spaceship: Phaser.GameObjects.Sprite;
    openingDoor: boolean;
    gameOver: boolean;

    constructor() {
        super({
            key: "MainScene"
        });
        bindAllOfThis(this, MainScene.prototype);
    }

    preload(): void {
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/program_robot.json"));
        this.load.image('background', require('/src/assets/qci/backgrounds/2i_background.png'));
        this.load.spritesheet("buttons", require("/src/assets/qci/sprites/gameProgramRobot/buttons.png"), { frameWidth: 88, frameHeight: 88 });
        this.load.spritesheet("robot", require("/src/assets/qci/sprites/gameProgramRobot/robot.png"), { frameWidth: 105, frameHeight: 250 });
        this.load.spritesheet("flames", require("/src/assets/qci/sprites/gameProgramRobot/rocket_flames.png"), { frameWidth: 285, frameHeight: 363 });
        this.load.spritesheet("spaceship", require("/src/assets/qci/sprites/gameProgramRobot/spaceship.png"), { frameWidth: 285, frameHeight: 363 });
        this.load.image('console', require('/src/assets/qci/sprites/gameProgramRobot/console.png'));
        this.load.image('crystal', require('/src/assets/qci/sprites/gameProgramRobot/crystal.png'));
    }


    initialize(): void {
        this.map = this.make.tilemap({ key: 'map' });
        this.commandSlots = [];
        this.commands = [];
        this.arrows = [];
        this.openingDoor = false;
        this.gameOver = false;
    }

    create(): void {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.initialize();

        var background = this.add.image(0, 0, "background");
        background.setOrigin(0);
        background.setDisplaySize(this.gameWidth, this.gameHeight);
        this.createScene();
        this.addArrows();
        this.addCommandSlots();
        this.registerEvents();
        initializeInteractiveCursor(this);
    }

    registerEvents(): void {
        this.input.on('dragstart', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType) => {
            this.children.bringToTop(gameObject);
        });

        this.input.on('drag', (pointer: Phaser.Input.Pointer, gameObject: PhaserGeneralGameObjectType, dragX: number, dragY: number) => {
            gameObject.x = dragX;
            gameObject.y = dragY;
        });

        this.input.on('dragend', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, dropped: boolean) => {
            if (!dropped) {
                var arrow = this.getArrow(gameObject);
                this.putArrowBackInPile(arrow);
            }
        });

        this.input.on('drop', (pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Image, commandSlot: CommandSlot) => {
            this.children.bringToTop(gameObject);
            var arrow = this.getArrow(gameObject);
            this.acceptArrowInSlot(arrow, commandSlot);
        });
    }

    /**
     * Adds up, down, left, right arrows as "pick up zones"
     * Actually just an image and a draggable image
     */
    addArrows(): void {
        var arrowId = 0;
        this.map.getObjectLayer("Pickup").objects.forEach((ob: TiledGameObject) => {
            var frame, id;
            if (ob.name === "up") {
                frame = 0;
                id = 1;
            }
            else if (ob.name === "down") {
                frame = 2;
                id = 2;
            }
            else if (ob.name === "left") {
                frame = 3;
                id = 3;
            }
            else if (ob.name === "right") {
                frame = 1;
                id = 4;
            }

            // Picture of the arrow that doesn't move
            var placeholderArrow = this.add.image(ob.x, ob.y, "buttons", frame);
            placeholderArrow.setOrigin(0);

            // Draggable arrow on top
            var object = this.add.image(ob.x, ob.y, "buttons", frame);
            object.setOrigin(0);
            object.setName("arrow" + arrowId);
            object.type = ob.name;
            object.setInteractive();
            object.setDataEnabled();
            this.input.setDraggable(object);

            var command = new Command(id, ob.name, new Phaser.Geom.Point(placeholderArrow.x, placeholderArrow.y));
            var arrow = new Arrow(arrowId, command, object);
            this.arrows.push(arrow);
            this.commands.push(command);
            arrowId++;
        });
    }

    /**
     * Adds CommandSlots (Zones) labeled with index, starting as "unfilled"
     */
    addCommandSlots(): void {
        var i = 1;
        this.map.getObjectLayer('Answers').objects.forEach((ob: TiledGameObject) => {
            var commandSlot = new CommandSlot(this, ob.x + ob.width / 2, ob.y + ob.height / 2, ob.width, ob.height, i);
            commandSlot.setCircleDropZone(88);
            commandSlot.filled = false;

            commandSlot.setName("slot" + i);

            var image = this.add.image(commandSlot.x, commandSlot.y, "buttons", 0);
            image.setVisible(false);
            commandSlot.image = image;

            // Show index of command on the answer slot
            var style = { fontSize: 38, fontFamily: "Russo One, Arial" };
            var commandIndex = this.add.text(ob.x, ob.y, i + "", style);
            commandSlot.textObject = commandIndex;

            Phaser.Display.Align.In.Center(commandIndex, commandSlot);

            this.commandSlots.push(commandSlot);
            i++;
        });
    }

    acceptedArrow(arrow: Arrow, commandSlot: CommandSlot): boolean {
        return (!commandSlot.filled) ||
            (commandSlot.filled && arrow.command.direction !== commandSlot.arrow.command.direction);
    }

    /**
     * Called when a CommandSlot has accepted an arrow
     */
    acceptArrowInSlot(arrow: Arrow, commandSlot: CommandSlot): void {
        // Either it's not filled yet, or it already has the same arrow there
        if (!commandSlot.filled || (commandSlot.filled && commandSlot.arrow.command.direction === arrow.command.direction)) {
            arrow.object.x = arrow.command.pilePosition.x;
            arrow.object.y = arrow.command.pilePosition.y;
        }
        // It's filled and has a different arrow there
        else if (commandSlot.filled && commandSlot.arrow.command.direction !== arrow.command.direction) {
            // Put it back in the pile
            this.putArrowBackInPile(arrow);
        }

        // Update this slot
        commandSlot.arrow = arrow;
        commandSlot.filled = true;

        commandSlot.image.setFrame(arrow.object.frame.name);
        commandSlot.image.setVisible(true);
        this.children.bringToTop(commandSlot.image);

        // Update this arrow
        arrow.slot = commandSlot;
    }

    putArrowBackInPile(arrow: Arrow): void {
        var goTox, goToy;
        goTox = arrow.command.pilePosition.x;
        goToy = arrow.command.pilePosition.y;

        var object = arrow.object;
        // Put the arrow back
        if (!this.tweens.isTweening(object)) {
            this.tweens.add({
                targets: object,
                x: goTox,
                y: goToy,
                duration: 800,
                ease: Phaser.Math.Easing.Back.Out,
            });
        }
    }

    createScene(): void {
        var consoleArea = this.add.image(0, 0, "console");
        consoleArea.setOrigin(0);
        consoleArea.setY(this.gameHeight - consoleArea.height);

        this.map.getObjectLayer("Objects").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "robot") {
                this.robot = this.add.sprite(ob.x, ob.y, "robot", 0);
                this.robot.setOrigin(0);
                this.robot.setScale(0.5);

                this.anims.create({
                    key: "bob",
                    frames: this.anims.generateFrameNumbers("robot", { start: 0, end: 4 }),
                    frameRate: 4,
                    repeat: -1,
                });
                this.robot.anims.play("bob");

                this.anims.create({
                    key: "turn",
                    frames: [
                        { key: "robot", frame: 0 },
                        { key: "robot", frame: 5 },
                        { key: "robot", frame: 15 },
                    ],
                    frameRate: 8
                });
            }
            else if (ob.name === "spaceship") {
                this.spaceship = this.add.sprite(ob.x, ob.y, "spaceship", 6);
                this.spaceship.setOrigin(0);

                this.anims.create({
                    key: "openDoor",
                    frames: [
                        { key: "spaceship", frame: 6 },
                        { key: "spaceship", frame: 5 },
                        { key: "spaceship", frame: 4 },
                        { key: "spaceship", frame: 3 },
                        { key: "spaceship", frame: 2 },
                        { key: "spaceship", frame: 1 },
                        { key: "spaceship", frame: 0 },
                    ],
                    frameRate: 8
                });

                this.spaceship.on("animationcomplete", () => {
                    setTimeout(() => {
                        // End game
                        this.gameOver = true;
                        this.input.setDefaultCursor("default");
                        this.input.removeAllListeners();
                        this.scene.add("EndGameScene", EndGameScene, true);
                        this.scene.remove(this);
                    }, 1000);
                });
            }
            else if (ob.name === "button") {
                this.doneButton = this.add.sprite(ob.x, ob.y, "buttons", 5);
                this.doneButton.setOrigin(0);
                this.doneButton.setInteractive({ useHandCursor: true });

                this.doneButton.on("pointerover", () => {
                    this.doneButton.setFrame(4);
                });
                this.doneButton.on("pointerout", () => {
                    this.doneButton.setFrame(5);
                });
                this.doneButton.on("pointerdown", () => {
                    this.doneButton.disableInteractive();
                    this.checkCommands();
                });
            }
            else if (ob.name === "remove") {
                this.deleteArea = new Phaser.Geom.Rectangle(ob.x, ob.y, ob.width, ob.height);
            }
            else if (ob.name === "crystal") {
                var crystal = this.add.image(ob.x, ob.y, "crystal");
                crystal.setOrigin(0);
                crystal.setDepth(5);
            }
        });
    }

    getArrow(gameObject: Phaser.GameObjects.Image): Arrow {
        var arrow = this.arrows.find((ar: Arrow) => "arrow" + ar.arrowId === gameObject.name);
        return arrow;
    }

    drawPolygon(polygon: Phaser.Geom.Polygon): void {
        var graphics = this.add.graphics();
        graphics.fillStyle(0x000000, 1);
        graphics.beginPath();
        graphics.moveTo(polygon.points[0].x, polygon.points[0].y);
        for (var j = 1; j < polygon.points.length; j++) {
            graphics.lineTo(polygon.points[j].x, polygon.points[j].y);
        }
        graphics.closePath();
        graphics.fillPath();
    }

    checkCommands(): void {
        var correctCommands = Command.CORRECT_COMMANDS;
        if (this.getNumberOfCommands() !== correctCommands.length) {
            // Need to fill in more commands
            this.modal("incomplete");
            return;
        }

        for (var i = 0; i < correctCommands.length; i++) {
            if (this.commandSlots[i].arrow.command.id !== correctCommands[i].id) {
                // Incorrect command
                this.modal('incorrect', false, true);
                return;
            }
        }

        this.modal("correct", true, false);
    }

    /**
     * Will go through the student's commands and remove the wrong ones but keep the correct ones
     */
    removeWrongOnes(): void {
        var correctCommands = Command.CORRECT_COMMANDS;
        for (var i = 0; i < correctCommands.length; i++) {
            var commandSlot = this.commandSlots[i];
            if (commandSlot.arrow.command.id !== correctCommands[i].id) {
                // Incorrect command
                commandSlot.image.setVisible(false);
                commandSlot.arrow = null;
                commandSlot.filled = false;
            }
        }
    }

    getNumberOfCommands(): number {
        var count = 0;
        this.commandSlots.forEach((cs: CommandSlot) => {
            if (cs.filled) {
                count++;
            }
        });
        return count;
    }

    modal(translationKey: string, correct?: boolean, helpPlayer?: boolean) {
        var text = TranslateApi.translate("ProgramRobot.Phaser." + translationKey);
        var buttonConfig: PhaserButtonConfig = {
            smartSize: true,
            backgroundColor: Color.KUDER_SECONDARY_BUTTON
        };

        var config: PhaserModalConfig = {
            title: text,
            buttonWidth: 200,
            buttonConfig: buttonConfig
        };

        var buttonText;
        if (translationKey === "correct") {
            buttonText = TranslateApi.translate("ProgramRobot.Phaser.run");
        }
        else {
            buttonText = TranslateApi.translate("ProgramRobot.Phaser.tryAgain");
        }

        var options = [{ id: 1, displayText: buttonText }];
        var modal = new PhaserModal(this, 0, 0, options, config);
        modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
        modal.on("optionClicked", () => {
            modal.destroy();
            this.doneButton.setInteractive();
            if (helpPlayer) {
                this.removeWrongOnes();
            }
            if (correct) {
                // Game is over, play animation of robot
                this.playAnimation();
            }
        });
    }

    playAnimation(): void {
        this.disableEverything();
        this.children.bringToTop(this.robot);

        this.map.getObjectLayer("Path").objects.forEach((ob: TiledGameObject) => {
            this.points = [];
            this.points = ob["polyline"];
            this.points.forEach((p) => {
                p.x += ob.x;
                p.y += ob.y;
            });

            var robotPath = new Phaser.Curves.Path(this.robot.x + this.robot.displayWidth / 2, this.robot.y + this.robot.displayHeight);
            robotPath.splineTo(this.points);

            this.follower = this.add.follower(robotPath, this.robot.x + this.robot.displayWidth / 2, this.robot.y + this.robot.displayHeight / 2, "robot", 0);
            this.follower.setDepth(3);
            this.follower.setScale(0.5);
            this.robot.setVisible(false);

            this.anims.play("turn", this.follower);
            this.follower.on("animationcomplete", () => {
                this.follower.startFollow({
                    duration: 10000,
                } as PathConfig);
                this.follower.stopFollow();
            });
        
        });
    }

    update() {
        if (this.gameOver) {
            return;
        }

        if (this.follower != null) {
            if (this.follower.isFollowing()) {
                this.animateRobotAndSpaceShip();
            }
        }
    }

    animateRobotAndSpaceShip() {
        if (
            (this.follower.x > 95 && this.follower.x < 370) ||
            (this.follower.x > 400 && this.follower.x < 675) ||
            (this.follower.x > 713 && this.follower.x < 988) ||
            (this.follower.x > 1015 && this.follower.x < 1175)
        ) {
            this.follower.setFrame(5);
        }

        if (
            (this.follower.x < 95) ||
            (this.follower.x > 370 && this.follower.x < 400) ||
            (this.follower.x > 1175)
        ) {
            this.follower.setFrame(15);
        }

        if (
            (this.follower.x > 675 && this.follower.x < 713) ||
            (this.follower.x > 988 && this.follower.x < 1015)
        ) {
            this.follower.setFrame(0);
        }

        if (this.follower.x > 1015 && !this.openingDoor) {
            this.spaceship.anims.play("openDoor");
            this.openingDoor = true;
        }
    }

    disableEverything() {
        this.doneButton.disableInteractive();
        this.arrows.forEach((a: Arrow) => {
            a.object.input.enabled = false;
        });
    }
}



// WEBPACK FOOTER //
// ./src/games/ProgramRobotGame/scenes/MainScene.ts